import { required, useInput, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { Create } from "@/components/admin/create";
import { Edit } from "@/components/admin/edit";
import { FileInput } from "@/components/admin/file-input";
import { ReferenceArrayInput } from "@/components/admin/reference-array-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextArrayInput } from "@/components/admin/text-array-input";
import { TextInput } from "@/components/admin/text-input";
import { Card, CardContent } from "@/components/ui/card";

import { AttachmentField } from "../../atomic-crm/notes/AttachmentField";
import { PROJECT_TYPES, STYLE_STATUSES } from "../common/constants";
import type { StyleTokens } from "../types";
import { StylePreview } from "./StylePreview";
import { COLOR_KEYS, FONT_CHOICES, RADIUS_CHOICES } from "./tokens";

const ColorInput = ({ source, label }: { source: string; label: string }) => {
  const { field } = useInput({ source });
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="color"
        value={field.value || "#000000"}
        onChange={(e) => field.onChange(e.target.value)}
        className="size-9 cursor-pointer rounded border bg-transparent p-0.5"
      />
      <span className="flex flex-col">
        <span>{label}</span>
        <input
          value={field.value ?? ""}
          onChange={(e) => field.onChange(e.target.value)}
          className="w-24 bg-transparent font-mono text-xs text-muted-foreground outline-none"
        />
      </span>
    </label>
  );
};

const LivePreview = () => {
  const tokens = useWatch({ name: "tokens" }) as StyleTokens | undefined;
  return <StylePreview tokens={tokens} />;
};

const StyleInputs = () => {
  const translate = useTranslate();
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,420px)_1fr]">
      <div className="flex flex-col gap-4">
        <TextInput source="name" validate={required()} />
        <TextInput source="description" multiline rows={3} />
        <div className="grid gap-4 sm:grid-cols-2 [&>*]:min-w-0 [&>*>*]:min-w-0">
          <SelectInput
            source="status"
            choices={STYLE_STATUSES}
            validate={required()}
          />
          <AutocompleteArrayInput
            source="project_types"
            choices={PROJECT_TYPES}
          />
        </div>
        <TextArrayInput source="tags" />
        <h4 className="pt-2 font-semibold">{translate("hub.styles.colors")}</h4>
        <div className="grid grid-cols-2 gap-3">
          {COLOR_KEYS.map((key) => (
            <ColorInput
              key={key}
              source={`tokens.colors.${key}`}
              label={translate(`hub.styles.color.${key}`)}
            />
          ))}
        </div>
        <h4 className="pt-2 font-semibold">
          {translate("hub.styles.typography")}
        </h4>
        <div className="grid gap-4 sm:grid-cols-2 [&>*]:min-w-0 [&>*>*]:min-w-0">
          <SelectInput
            source="tokens.fonts.heading"
            label="hub.styles.heading_font"
            choices={FONT_CHOICES}
            translateChoice={false}
          />
          <SelectInput
            source="tokens.fonts.body"
            label="hub.styles.body_font"
            choices={FONT_CHOICES}
            translateChoice={false}
          />
          <SelectInput
            source="tokens.radius"
            label="hub.styles.radius"
            choices={RADIUS_CHOICES}
            translateChoice={false}
          />
          <SelectInput
            source="tokens.mode"
            label="hub.styles.mode"
            choices={[
              { id: "light", name: "hub.styles.light" },
              { id: "dark", name: "hub.styles.dark" },
            ]}
          />
        </div>
        <TextInput source="demo_url" />
        <ReferenceArrayInput source="deal_ids" reference="deals">
          <AutocompleteArrayInput
            label="resources.styles.fields.deal_ids"
            optionText="name"
          />
        </ReferenceArrayInput>
        <FileInput source="images" multiple accept={{ "image/*": [] }}>
          <AttachmentField source="src" title="title" target="_blank" />
        </FileInput>
      </div>
      <div className="xl:sticky xl:top-4 xl:self-start">
        <h4 className="mb-2 font-semibold">
          {translate("hub.styles.preview")}
        </h4>
        <LivePreview />
      </div>
    </div>
  );
};

const defaults = {
  status: "idea",
  project_types: ["crm"],
  tags: [],
  images: [],
  deal_ids: [],
  tokens: {
    colors: {
      primary: "#0B2545",
      secondary: "#E2E8F0",
      accent: "#8DA9C4",
      background: "#F8FAFC",
      surface: "#FFFFFF",
      foreground: "#0F172A",
      muted: "#64748B",
      border: "#E2E8F0",
    },
    fonts: { heading: "Inter", body: "Inter" },
    radius: "0.5rem",
    mode: "light",
  },
};

export const StyleCreate = () => (
  <Create resource="styles" redirect="show" className="w-full">
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none" defaultValues={defaults}>
          <StyleInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Create>
);

export const StyleEdit = () => (
  <Edit
    resource="styles"
    redirect="show"
    mutationMode="pessimistic"
    className="w-full"
  >
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none">
          <StyleInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Edit>
);
